import React, { useState, useRef, forwardRef, useEffect } from "react";
import "./Calendar.css";

// Хук для обработки кликов вне компонента
const useClickOutside = (handler) => {
  const ref = useRef();

  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [handler]);

  return ref;
};

const Calendar = forwardRef(
  (
    { value, onChange, maxDate, minDate, placeholder = "Выберите дату" },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(() => {
      if (value) {
        // Преобразуем входную дату в UTC
        const date = new Date(value);
        return new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            12,
            0,
            0
          )
        );
      }
      // Для текущей даты также используем UTC
      const now = new Date();
      return new Date(
        Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0)
      );
    });
    const calendarRef = useClickOutside(() => setIsOpen(false));

    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];

    const currentYear = new Date().getFullYear();
    // Создаем массив будущих лет (3 года вперед)
    const futureYears = Array.from(
      { length: 3 },
      (_, i) => currentYear + i + 1
    );
    // Создаем массив прошлых лет (100 лет назад)
    const pastYears = Array.from({ length: 100 }, (_, i) => currentYear - i);
    // Объединяем и сортируем по убыванию (будущие годы первыми)
    const years = [...futureYears, ...pastYears].sort((a, b) => b - a);

    const handleDateSelect = (date) => {
      if (maxDate && date > maxDate) return;
      if (minDate && date < minDate) return;

      // Создаем дату в UTC, чтобы избежать проблем с часовым поясом
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      // Создаем дату в UTC, устанавливая время на полдень
      const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));

      onChange(utcDate);
      setIsOpen(false);
    };

    const formatDate = (date) => {
      if (!date) return "";

      // Преобразуем UTC дату в локальную дату для отображения
      const localDate = new Date(date);

      return localDate.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    const getDaysInMonth = (year, month) => {
      return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year, month) => {
      return new Date(year, month, 1).getDay();
    };

    const isToday = (date) => {
      const today = new Date();
      const todayUTC = new Date(
        Date.UTC(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          12,
          0,
          0
        )
      );

      return (
        date.getUTCDate() === todayUTC.getUTCDate() &&
        date.getUTCMonth() === todayUTC.getUTCMonth() &&
        date.getUTCFullYear() === todayUTC.getUTCFullYear()
      );
    };

    const isSelected = (date) => {
      if (!value) return false;

      return (
        date.getUTCDate() === value.getUTCDate() &&
        date.getUTCMonth() === value.getUTCMonth() &&
        date.getUTCFullYear() === value.getUTCFullYear()
      );
    };

    const isDisabled = (date) => {
      if (maxDate) {
        const maxDateUTC = new Date(
          Date.UTC(
            maxDate.getFullYear(),
            maxDate.getMonth(),
            maxDate.getDate(),
            12,
            0,
            0
          )
        );
        if (date > maxDateUTC) return true;
      }

      if (minDate) {
        const minDateUTC = new Date(
          Date.UTC(
            minDate.getFullYear(),
            minDate.getMonth(),
            minDate.getDate(),
            12,
            0,
            0
          )
        );
        if (date < minDateUTC) return true;
      }

      return false;
    };

    const renderCalendar = () => {
      const year = currentDate.getUTCFullYear();
      const month = currentDate.getUTCMonth();
      const daysInMonth = getDaysInMonth(year, month);
      const firstDay = getFirstDayOfMonth(year, month);
      const days = [];

      // Добавляем пустые ячейки для выравнивания календаря
      for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
        days.push(
          <div key={`empty-${i}`} className="calendar-day empty"></div>
        );
      }

      // Добавляем дни месяца
      for (let day = 1; day <= daysInMonth; day++) {
        // Создаем дату в UTC, чтобы избежать проблем с часовым поясом
        const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
        const classes = [
          "calendar-day",
          isToday(date) ? "today" : "",
          isSelected(date) ? "selected" : "",
          isDisabled(date) ? "disabled" : "",
        ]
          .filter(Boolean)
          .join(" ");

        days.push(
          <div
            key={day}
            className={classes}
            onClick={() => !isDisabled(date) && handleDateSelect(date)}
          >
            {day}
          </div>
        );
      }

      return days;
    };

    return (
      <div className="calendar-wrapper" ref={calendarRef}>
        <input
          ref={ref}
          className="registration__input"
          value={value ? formatDate(value) : ""}
          onClick={() => setIsOpen(true)}
          readOnly
          placeholder={placeholder}
        />
        {isOpen && (
          <div className="calendar-popup">
            <div className="calendar-header">
              <select
                value={currentDate.getUTCMonth()}
                onChange={(e) =>
                  setCurrentDate(
                    new Date(
                      Date.UTC(
                        currentDate.getUTCFullYear(),
                        parseInt(e.target.value),
                        1,
                        12,
                        0,
                        0
                      )
                    )
                  )
                }
              >
                {months.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={currentDate.getUTCFullYear()}
                onChange={(e) =>
                  setCurrentDate(
                    new Date(
                      Date.UTC(
                        parseInt(e.target.value),
                        currentDate.getUTCMonth(),
                        1,
                        12,
                        0,
                        0
                      )
                    )
                  )
                }
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="calendar-days-header">
              <div>Пн</div>
              <div>Вт</div>
              <div>Ср</div>
              <div>Чт</div>
              <div>Пт</div>
              <div>Сб</div>
              <div>Вс</div>
            </div>
            <div className="calendar-days">{renderCalendar()}</div>
          </div>
        )}
      </div>
    );
  }
);

Calendar.displayName = "Calendar";

export default Calendar;
